import React, { useState, useEffect } from 'react';
import { X, Pin, PinOff, ChevronDown, ChevronUp } from 'lucide-react';
import axios from 'axios';

const AlertItem = ({ alert, isDarkMode, onDismiss, onPin, formatTime, edgeServerEndpoint, timezone, timeFormat = '24h' }) => {
  // Information insights (chatbot responses) are expanded by default
  const [isExpanded, setIsExpanded] = useState(alert.category === 'Information' || alert.isInformation || false);
  const [timeRemaining, setTimeRemaining] = useState(null);
  const [isDismissing, setIsDismissing] = useState(false);
  const [isPinning, setIsPinning] = useState(false);

  // Format alert time - handles both ISO 8601 and YYYYMMDD-HHMMSS formats
  // Displays in local time based on timezone setting, respects 12h/24h format
  const formatAlertTime = (timeString) => {
    if (!timeString) return 'No time';
    
    try {
      let date;
      
      // Try to parse as ISO 8601 first (most common for alerts)
      if (timeString.includes('T') || timeString.includes('Z') || timeString.includes('+') || timeString.includes('-')) {
        date = new Date(timeString);
      } else if (timeString.match(/^\d{8}-\d{6}$/)) {
        // Parse YYYYMMDD-HHMMSS format
        const year = parseInt(timeString.substring(0, 4));
        const month = parseInt(timeString.substring(4, 6)) - 1;
        const day = parseInt(timeString.substring(6, 8));
        const hours = parseInt(timeString.substring(9, 11));
        const minutes = parseInt(timeString.substring(11, 13));
        const seconds = parseInt(timeString.substring(13, 15));
        date = new Date(Date.UTC(year, month, day, hours, minutes, seconds));
      } else {
        // Try generic Date parsing
        date = new Date(timeString);
      }
      
      // Check if date is valid
      if (!date || isNaN(date.getTime())) {
        // If parsing failed, try to extract just the time part if it's in a readable format
        const timeMatch = timeString.match(/(\d{1,2}):(\d{2})(?::(\d{2}))?/);
        if (timeMatch) {
          return timeString; // Return original if it looks like a time string
        }
        return timeString; // Return original string if can't parse
      }
      
      // Format based on timezone and timeFormat preference
      const tz = timezone || 'America/Chicago';
      const use12Hour = timeFormat === '12h';
      
      // Create a formatter for the specified timezone
      const formatter = new Intl.DateTimeFormat('en-US', {
        timeZone: tz,
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        hour12: use12Hour
      });
      
      // Format the date in the specified timezone (time only)
      const formatted = formatter.format(date);
      
      // Check if formatting resulted in "Invalid Date"
      if (formatted.includes('Invalid') || formatted.includes('NaN')) {
        return timeString; // Return original if formatting failed
      }
      
      return formatted;
    } catch (error) {
      console.error('Error formatting alert time:', error, timeString);
      return timeString; // Return original on error
    }
  };

  // Calculate time remaining for timeout
  useEffect(() => {
    if (alert.timeout > 0 && !alert.sticky && !alert.pinned) {
      const calculateTimeRemaining = () => {
        try {
          const createdTime = new Date(alert.created_at).getTime();
          const now = Date.now();
          const elapsed = Math.floor((now - createdTime) / 1000);
          const remaining = alert.timeout - elapsed;
          
          if (remaining <= 0) {
            setTimeRemaining(0);
            // Auto-dismiss if timeout expired
            if (!isDismissing) {
              handleDismiss();
            }
          } else {
            setTimeRemaining(remaining);
          }
        } catch (error) {
          console.error('Error calculating time remaining:', error);
        }
      };

      calculateTimeRemaining();
      const interval = setInterval(calculateTimeRemaining, 1000);
      return () => clearInterval(interval);
    }
  }, [alert.timeout, alert.sticky, alert.pinned, alert.created_at, isDismissing]);

  const handleDismiss = async () => {
    if (isDismissing) return;
    setIsDismissing(true);
    try {
      await axios.put(`${edgeServerEndpoint}/alerts/${alert.id}/dismiss`);
      if (onDismiss) {
        onDismiss(alert.id);
      }
    } catch (error) {
      console.error('Error dismissing alert:', error);
      setIsDismissing(false);
    }
  };

  const handlePin = async () => {
    if (isPinning) return;
    setIsPinning(true);
    try {
      const newPinnedState = !alert.pinned;
      await axios.put(`${edgeServerEndpoint}/alerts/${alert.id}/pin`, {
        pinned: newPinnedState
      });
      if (onPin) {
        onPin(alert.id, newPinnedState);
      }
    } catch (error) {
      console.error('Error pinning/unpinning alert:', error);
    } finally {
      setIsPinning(false);
    }
  };

  // Priority color mapping
  const getPriorityColors = (priority) => {
    const colors = {
      Critical: {
        bg: isDarkMode ? 'bg-red-900/20' : 'bg-red-50',
        border: 'border-red-500',
        text: isDarkMode ? 'text-red-200' : 'text-red-900',
        badge: isDarkMode ? 'bg-red-800 text-red-100' : 'bg-red-500 text-white'
      },
      High: {
        bg: isDarkMode ? 'bg-orange-900/20' : 'bg-orange-50',
        border: 'border-orange-500',
        text: isDarkMode ? 'text-orange-200' : 'text-orange-900',
        badge: isDarkMode ? 'bg-orange-800 text-orange-100' : 'bg-orange-500 text-white'
      },
      Medium: {
        bg: isDarkMode ? 'bg-yellow-900/20' : 'bg-yellow-50',
        border: 'border-yellow-500',
        text: isDarkMode ? 'text-yellow-200' : 'text-yellow-900',
        badge: isDarkMode ? 'bg-yellow-800 text-yellow-100' : 'bg-yellow-500 text-white'
      },
      Low: {
        bg: isDarkMode ? 'bg-blue-900/20' : 'bg-blue-50',
        border: 'border-blue-500',
        text: isDarkMode ? 'text-blue-200' : 'text-blue-900',
        badge: isDarkMode ? 'bg-blue-800 text-blue-100' : 'bg-blue-500 text-white'
      }
    };
    return colors[priority] || colors.Medium;
  };

  const colors = getPriorityColors(alert.priority);
  const hasDetails = alert.details && alert.details.trim().length > 0;

  return (
    <div
      className={`border-l-4 rounded-lg p-3 mb-2 transition-colors duration-150 ${colors.bg} ${colors.border} ${
        isDarkMode ? 'border-opacity-70' : 'border-opacity-100'
      }`}
    >
      {/* Header */}
      <div className="flex items-start justify-between gap-2">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            {alert.category === 'Information' && (
              <span className={`px-2 py-0.5 text-xs font-semibold rounded-full ${
                isDarkMode ? 'bg-purple-800 text-purple-100' : 'bg-purple-500 text-white'
              }`}>
                Information
              </span>
            )}
            {alert.category !== 'Information' && (
              <span className={`px-2 py-0.5 text-xs font-semibold rounded-full ${colors.badge}`}>
                {alert.priority}
              </span>
            )}
            {(alert.pinned || alert.sticky) && (
              <span className={`px-2 py-0.5 text-xs font-medium rounded-full ${
                isDarkMode ? 'bg-gray-700 text-gray-300' : 'bg-gray-200 text-gray-700'
              }`}>
                Pinned
              </span>
            )}
          </div>
          <h4 className={`font-semibold text-sm mb-1 ${colors.text}`}>
            {alert.title}
          </h4>
          <div className="flex items-center gap-2 text-xs text-gray-500">
            <span>{formatAlertTime(alert.time)}</span>
            {timeRemaining !== null && timeRemaining > 0 && (
              <span className={`px-1.5 py-0.5 rounded ${
                isDarkMode ? 'bg-gray-800 text-gray-400' : 'bg-gray-200 text-gray-600'
              }`}>
                {Math.floor(timeRemaining / 60)}:{(timeRemaining % 60).toString().padStart(2, '0')}
              </span>
            )}
          </div>
        </div>
        <div className="flex items-center gap-1 flex-shrink-0">
          <button
            onClick={handlePin}
            disabled={isPinning || alert.sticky}
            className={`p-1.5 rounded transition-colors ${
              (alert.pinned || alert.sticky)
                ? isDarkMode
                  ? 'bg-blue-800 text-blue-200 hover:bg-blue-700'
                  : 'bg-blue-100 text-blue-700 hover:bg-blue-200'
                : isDarkMode
                  ? 'text-gray-400 hover:bg-gray-800 hover:text-gray-300'
                  : 'text-gray-500 hover:bg-gray-200 hover:text-gray-700'
            }`}
            title={alert.sticky ? 'Sticky (cannot be unpinned)' : (alert.pinned ? 'Unpin alert' : 'Pin alert')}
          >
            {(alert.pinned || alert.sticky) ? <Pin size={16} /> : <PinOff size={16} />}
          </button>
          <button
            onClick={handleDismiss}
            disabled={isDismissing}
            className={`p-1.5 rounded transition-colors ${
              isDarkMode
                ? 'text-gray-400 hover:bg-gray-800 hover:text-gray-300'
                : 'text-gray-500 hover:bg-gray-200 hover:text-gray-700'
            }`}
            title="Dismiss alert"
          >
            <X size={16} />
          </button>
        </div>
      </div>

      {/* Details */}
      {hasDetails && (
        <div className="mt-2">
          {isExpanded ? (
            <div>
              <button
                onClick={() => setIsExpanded(false)}
                className={`flex items-center gap-1 text-xs mb-2 ${
                  isDarkMode ? 'text-gray-400 hover:text-gray-300' : 'text-gray-600 hover:text-gray-800'
                }`}
              >
                <ChevronUp size={14} />
                Hide details
              </button>
              <p className={`text-sm ${colors.text} whitespace-pre-wrap`}>
                {alert.details}
              </p>
            </div>
          ) : (
            <button
              onClick={() => setIsExpanded(true)}
              className={`flex items-center gap-1 text-xs ${
                isDarkMode ? 'text-gray-400 hover:text-gray-300' : 'text-gray-600 hover:text-gray-800'
              }`}
            >
              <ChevronDown size={14} />
              Show details
            </button>
          )}
        </div>
      )}
    </div>
  );
};

export default AlertItem;
